import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Route-aware robots directives.
 *
 * Three private routes cannot emit their own directive from the page component:
 * ProtectedRoute redirects an anonymous visitor before the page ever mounts, so
 * a per-page SEO component never runs for them. They are managed here instead,
 * keyed off the current route.
 *
 * WHY THIS EXISTS
 * ---------------
 * A directive appended once at document load is never removed, so leaving one of
 * these routes by client-side navigation used to carry `noindex` onto a public
 * page — the exact failure that silently deindexes pages. This module owns the
 * tag for the whole session: it adds, updates and removes it as the route
 * changes, so Back and Forward are covered too.
 *
 * WHAT IT TOUCHES
 * ---------------
 * Only the tag it created, identified by ROUTE_ROBOTS_ATTR. A directive managed
 * by another SEO component (src/components/SEO.jsx, src/Layout.jsx) is never
 * modified or removed. When an identical directive is already present it is
 * reused rather than duplicated, so no route ever ends up with two conflicting
 * `robots` tags. react-helmet-async emits at most one robots tag per page, and
 * this module never adds a second one alongside it.
 *
 * index.html carries the same two directives for the initial document, so the
 * crawler-facing HTML is covered before React boots. That tag is marked with the
 * same attribute, so this module adopts it and takes over its lifecycle from
 * there. Keep the two tables in step.
 */
export const ROUTE_ROBOTS_ATTR = 'data-clinivo-route-robots';

/** The only three routes this module manages. Every other route is left alone. */
const ROUTE_DIRECTIVES = {
  '/jobfeedreconciliation': { content: 'noindex, nofollow', pageName: 'JobFeedReconciliation' },
  '/seofreshness': { content: 'noindex, nofollow', pageName: 'SeoFreshness' },
  '/onboarding': { content: 'noindex, follow', pageName: 'Onboarding' },
};

/**
 * Page names owned by this module. The shared layout skips its own blanket
 * noindex for these, so the route ends up with exactly ONE robots directive
 * instead of two identical ones.
 */
export const ROUTE_ROBOTS_PAGE_NAMES = Object.values(ROUTE_DIRECTIVES).map((entry) => entry.pageName);

export function normalizeRobotsPath(pathname) {
  const trimmed = String(pathname == null ? '/' : pathname).replace(/\/+$/, '').toLowerCase();
  return trimmed || '/';
}

/** The directive a route requires, or null when it is not managed here. */
export function robotsDirectiveFor(pathname) {
  const entry = ROUTE_DIRECTIVES[normalizeRobotsPath(pathname)];
  return entry ? entry.content : null;
}

function ownedTag() {
  return document.head.querySelector(`meta[${ROUTE_ROBOTS_ATTR}]`);
}

/**
 * Bring the document in line with `pathname`. Returns what it did, which is what
 * the release checks assert against.
 */
export function syncRouteRobots(pathname) {
  const desired = robotsDirectiveFor(pathname);
  const owned = ownedTag();

  if (!desired) {
    // Leaving a managed route: remove only the tag this module owns. Anything
    // another SEO component emitted is its own to remove.
    if (owned) owned.remove();
    return 'removed';
  }

  if (owned) {
    if (owned.getAttribute('content') !== desired) {
      owned.setAttribute('content', desired);
      return 'updated';
    }
    return 'unchanged';
  }

  // Another component may already be emitting an identical directive for this
  // route. Reuse it rather than adding a second robots tag.
  const present = Array.from(document.head.querySelectorAll('meta[name="robots"]'));
  if (present.some((tag) => (tag.getAttribute('content') || '').trim() === desired)) {
    return 'already-present';
  }

  const meta = document.createElement('meta');
  meta.setAttribute('name', 'robots');
  meta.setAttribute('content', desired);
  meta.setAttribute(ROUTE_ROBOTS_ATTR, 'true');
  document.head.appendChild(meta);
  return 'added';
}

export default function RouteRobots() {
  const { pathname } = useLocation();

  useEffect(() => {
    syncRouteRobots(pathname);
  }, [pathname]);

  // A full teardown must not leave the directive behind.
  useEffect(() => () => {
    const owned = ownedTag();
    if (owned) owned.remove();
  }, []);

  return null;
}